import Reveal from "@/components/Reveal";
import { TESTIMONIALS } from "@/content";

const labelCls = "text-[11px] font-semibold tracking-[0.3em] uppercase text-fog";

function Attribution({ t }) {
  const location = [t.city, t.state].filter(Boolean).join(", ");
  return (
    <div className="mt-5">
      <p className="font-spartan font-bold uppercase tracking-wide text-sm">{t.name}</p>
      {t.title && (
        <p className="mt-1 text-xs tracking-[0.15em] uppercase text-fog">{t.title}</p>
      )}
      {t.organization && (
        <p className="mt-0.5 text-xs tracking-[0.15em] uppercase text-fog">{t.organization}</p>
      )}
      {location && (
        <p className="mt-0.5 text-xs tracking-[0.15em] uppercase text-fog">{location}</p>
      )}
      {t.years && (
        <p className="mt-0.5 text-xs tracking-[0.15em] uppercase text-fog">{t.years}</p>
      )}
    </div>
  );
}

function StandardItem({ t, i }) {
  return (
    <article
      data-testid={`wall-card-${i}`}
      className="border-t border-ink/15 pt-7 flex gap-5 md:gap-6 items-start"
    >
      <img
        src={t.photo}
        alt={`Portrait of ${t.name}`}
        loading="lazy"
        style={{ objectPosition: t.photoPosition || "center" }}
        className="w-20 h-20 md:w-28 md:h-28 object-cover shrink-0"
      />
      <div className="min-w-0">
        <p className="text-sm md:text-base leading-relaxed text-charcoal/90">“{t.quote}”</p>
        <Attribution t={t} />
      </div>
    </article>
  );
}

function FeaturedItem({ t, i, flip }) {
  return (
    <article
      data-testid={`featured-testimonial-${i}`}
      className="border-t-2 border-ink pt-10 md:pt-12 grid grid-cols-1 md:grid-cols-12 gap-7 md:gap-12 items-start"
    >
      <div className={`md:col-span-4 ${flip ? "md:order-2" : ""}`}>
        <img
          src={t.photo}
          alt={`Portrait of ${t.name}`}
          loading="lazy"
          style={{ objectPosition: t.photoPosition || "center" }}
          className="w-full aspect-[16/10] md:aspect-[4/5] object-cover"
        />
      </div>
      <div className={`md:col-span-8 ${flip ? "md:order-1" : ""}`}>
        {t.excerpt && (
          <p className="font-spartan font-bold text-2xl md:text-3xl leading-[1.25] tracking-tight max-w-2xl">
            “{t.excerpt}”
          </p>
        )}
        <p className="mt-5 text-sm md:text-base leading-relaxed text-charcoal/90 max-w-2xl">
          “{t.quote}”
        </p>
        <Attribution t={t} />
      </div>
    </article>
  );
}

export default function Testimonials() {
  const visible = TESTIMONIALS.filter((t) => t.permission && t.name && t.photo && t.quote);
  const featured = visible.filter((t) => t.featured);
  const standard = visible.filter((t) => !t.featured);

  // Distribute featured moments through the flow: F → 2 standard → F → 2 standard → …
  const blocks = [];
  let fi = 0;
  let si = 0;
  if (featured.length) blocks.push({ kind: "featured", t: featured[fi++] });
  while (si < standard.length) {
    blocks.push({ kind: "group", items: standard.slice(si, si + 2) });
    si += 2;
    if (fi < featured.length) blocks.push({ kind: "featured", t: featured[fi++] });
  }

  let featuredIndex = 0;
  let standardIndex = 0;

  return (
    <div className="mt-16 md:mt-24" id="testimonials" data-testid="testimonials-section">
      <Reveal>
        <p className={labelCls}>From churches, teams & ministry leaders</p>
        <h3 className="mt-3 font-spartan font-extrabold uppercase display-type text-3xl md:text-5xl leading-[0.95]">
          Trusted by pastors, leaders & teams
        </h3>
        <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
          The best part of ministry has never been the platform. It’s the people you get to serve
          along the way. Here are a few words from leaders, pastors, teams, and friends David has
          had the privilege of serving alongside.
        </p>
      </Reveal>

      <div className="mt-12 md:mt-16 space-y-12 md:space-y-16">
        {blocks.map((block, bi) => {
          if (block.kind === "featured") {
            const thisFeatured = featuredIndex++;
            return (
              <Reveal key={`f-${block.t.name}`} delay={0.05}>
                <FeaturedItem t={block.t} i={thisFeatured} flip={thisFeatured % 2 === 1} />
              </Reveal>
            );
          }
          return (
            <div key={`g-${bi}`} className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
              {block.items.map((t) => (
                <Reveal key={t.name} delay={0.05}>
                  <StandardItem t={t} i={standardIndex++} />
                </Reveal>
              ))}
            </div>
          );
        })}
      </div>

      <Reveal className="mt-14 md:mt-20">
        <div data-testid="testimonials-cta" className="border-t border-ink/15 pt-12 md:pt-16">
          <h4 className="font-spartan font-extrabold uppercase display-type text-3xl md:text-4xl leading-[0.95]">
            Let’s serve your team next.
          </h4>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
            Every church and every team has a story, a culture, and a next step. If David can
            help your leaders navigate what’s next, he’d love to start a conversation.
          </p>
          <a
            href="#invite"
            data-testid="testimonials-invite-button"
            className="mt-8 inline-flex items-center justify-center gap-3 bg-ink text-paper font-semibold text-xs tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-charcoal transition-colors duration-300"
          >
            Invite David
          </a>
        </div>
      </Reveal>
    </div>
  );
}
